import { Activity, ArrowRight, Newspaper, Stethoscope, Syringe, Calculator, Utensils, Sparkles, Quote, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { SocialIconLink } from "./socialPlatforms";

// Auto-rotating hero variants — both tell the same story: peptides + nutrition
// working together.  Generated via Gemini 3 Pro Image in the Amino Exchange
// gunmetal + gold aesthetic.
const HERO_VARIANTS = [
  { key: "helix-greens",   src: "/hero/hero-helix-greens.png",   alt: "A chrome DNA helix threaded with gold peptide capsules, growing out of a bed of fresh leafy greens" },
  { key: "molecule-berry", src: "/hero/hero-molecule-berry.png", alt: "A chromed peptide molecular chain with amber-gold inclusions woven through fresh blueberries, blackberries, orange slices, and rosemary" },
];

const ROTATE_MS = 6500; // one crossfade every ~6.5 s

const VARIANT_STORAGE_KEY = "aminox_hero_variant";

/** Optional `?hero=<key>` query override for previewing a specific variant. */
function getForcedVariant() {
  if (typeof window === "undefined") return null;
  try {
    const q = new URLSearchParams(window.location.search).get("hero");
    return HERO_VARIANTS.find((v) => v.key === q) || null;
  } catch {
    return null;
  }
}

import { useTheme } from "../context/ThemeContext";

// Palette — light, airy, professional healthcare (dark values are swapped
// dynamically via useTheme() below so login/signup respect the device theme).
const BG        = "#FFFFFF";
const BG_SOFT   = "#F8FAFC";  // very light gray
const CARD      = "#FFFFFF";
const INK       = "#0F172A";  // slate-900
const INK_MUT   = "#64748B";  // slate-500
const INK_DIM   = "#94A3B8";  // slate-400
const BORDER    = "#E2E8F0";  // slate-200
const TEAL      = "#0D9488";  // teal-600
const TEAL_D    = "#0F766E";  // teal-700
const TEAL_SOFT = "#F0FDFA";  // teal-50

// Dark-mode palette overrides for LandingHero. Everywhere the component reads
// one of the CSS-var-shaped names below, we swap through `p` (the resolved
// palette) instead of the raw constant.
const DARK_PALETTE = {
    BG: "#0B1220", BG_SOFT: "#0F172A", CARD: "#111827",
    INK: "#F1F5F9", INK_MUT: "#94A3B8", INK_DIM: "#64748B",
    BORDER: "#1E293B",
    TEAL: "#14B8A6", TEAL_D: "#2DD4BF", TEAL_SOFT: "rgba(20,184,166,0.10)",
};
const LIGHT_PALETTE = { BG, BG_SOFT, CARD, INK, INK_MUT, INK_DIM, BORDER, TEAL, TEAL_D, TEAL_SOFT };

export default function LandingHero({ settings, previewMode = false, children }) {
  const { resolved: themeResolved } = useTheme();
  const p = themeResolved === "dark" ? DARK_PALETTE : LIGHT_PALETTE;
  const { BG, BG_SOFT, CARD, INK, INK_MUT, INK_DIM, BORDER, TEAL, TEAL_D, TEAL_SOFT } = p;
  const s = settings || {};
  const forced = getForcedVariant();
  const [heroIdx, setHeroIdx] = useState(() => {
    if (forced) return HERO_VARIANTS.findIndex((v) => v.key === forced.key);
    return 0;
  });

  // Auto-rotate through variants unless the user pinned one via ?hero=...
  useEffect(() => {
    if (forced || previewMode) return undefined;
    const t = setInterval(() => {
      setHeroIdx((i) => (i + 1) % HERO_VARIANTS.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [forced, previewMode]);

  // Stamp the currently-visible variant on localStorage so signup analytics
  // can attribute which hero the visitor saw when they converted.
  useEffect(() => {
    try {
      window.localStorage?.setItem(VARIANT_STORAGE_KEY, HERO_VARIANTS[heroIdx].key);
    } catch { /* private mode */ }
  }, [heroIdx]);

  const bg = s.hero_image_url || HERO_VARIANTS[heroIdx].src;
  const variant = HERO_VARIANTS[heroIdx];
  const news = Array.isArray(s.news_items) ? s.news_items.filter((n) => n && (n.title || n.body)) : [];
  const links = Array.isArray(s.social_links) ? s.social_links.filter((l) => l && l.platform && l.url) : [];

  return (
    <div
      className={`${previewMode ? "" : "min-h-screen"} flex flex-col`}
      style={{ background: BG, color: INK, fontFamily: "'Manrope', -apple-system, sans-serif" }}
      data-testid="landing-hero-root"
    >
      {/* Top nav */}
      <header className="w-full" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div className={`mx-auto flex items-center justify-between ${previewMode ? "px-6 py-4" : "max-w-6xl px-6 lg:px-8 py-5"}`}>
          <div className="flex items-center gap-3">
            {s.logo_url ? (
              <img src={s.logo_url} alt={`${s.site_name || "Site"} logo`} className="w-10 h-10 object-contain rounded-lg" data-testid="landing-brand-logo" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: TEAL_SOFT }}>
                <Stethoscope className="w-5 h-5" style={{ color: TEAL }} strokeWidth={2.2} />
              </div>
            )}
            <div className="leading-none">
              <div className="text-lg font-bold tracking-tight" style={{ color: INK }} data-testid="landing-site-name">
                {s.site_name || "The Amino Exchange"}
              </div>
              <div className="text-[11px] mt-1" style={{ color: INK_MUT }}>
                {s.tagline || "Precision peptide & nutrition tracking"}
              </div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <Link
              to="/signup"
              className="px-4 py-2 rounded-full text-sm font-semibold transition-colors"
              style={{ background: TEAL, color: "#FFFFFF" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = TEAL_D)}
              onMouseLeave={(e) => (e.currentTarget.style.background = TEAL)}
              data-testid="nav-get-started"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Main centered hero + form */}
      <main className="flex-1">
        <div className={`mx-auto ${previewMode ? "px-6 py-10" : "max-w-6xl px-6 lg:px-8 py-14 lg:py-20"}`}>
          {/* Hero photo strip — auto-rotating crossfade carousel */}
          <div
            className={`relative rounded-2xl overflow-hidden mb-10 lg:mb-14 ${previewMode ? "h-40" : "h-56 lg:h-72"}`}
            style={{
              boxShadow: "0 8px 40px rgba(15,23,42,0.06)",
              // Matching background so a portrait or narrow user-uploaded image
              // gets clean letterboxing instead of black bars or leaking the
              // page canvas through. Uses the theme's soft surface color.
              background: p.BG_SOFT,
            }}
            data-testid="landing-hero-carousel"
            data-hero-variant={variant.key}
          >
            {HERO_VARIANTS.map((v, i) => {
              // User-uploaded images use `object-contain` so the whole image is
              // always visible (no cropping/zooming/stretching). Stock editorial
              // photos keep `object-cover` for their intended full-bleed framing.
              const isUserUpload = !!(s.hero_image_url && i === heroIdx);
              const fit = isUserUpload ? "object-contain" : "object-cover";
              return (
                <img
                  key={v.key}
                  src={s.hero_image_url && i === heroIdx ? bg : v.src}
                  alt={v.alt}
                  className={`absolute inset-0 w-full h-full ${fit} transition-opacity duration-1000 ease-in-out ${i === heroIdx ? "opacity-100" : "opacity-0"}`}
                  data-testid={`landing-hero-image-${v.key}`}
                  data-hero-slide={v.key}
                  data-hero-fit={fit}
                  aria-hidden={i !== heroIdx}
                  loading={i === 0 ? "eager" : "lazy"}
                />
              );
            })}
            {/* Dot indicators — small, low-key */}
            {!previewMode && !getForcedVariant() && (
              <div
                className="absolute bottom-3 right-4 flex items-center gap-1.5 z-10"
                data-testid="landing-hero-dots"
              >
                {HERO_VARIANTS.map((v, i) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setHeroIdx(i)}
                    aria-label={`Show hero image ${i + 1}`}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${i === heroIdx ? "w-6" : "opacity-70 hover:opacity-100"}`}
                    style={{ background: i === heroIdx ? "#FFFFFF" : "rgba(255,255,255,0.6)", boxShadow: "0 1px 4px rgba(15,23,42,0.25)" }}
                    data-testid={`landing-hero-dot-${v.key}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Left: headline + copy + primary CTAs */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-5"
                style={{ background: TEAL_SOFT, color: TEAL }}
                data-testid="landing-intro-label"
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: TEAL }} />
                {s.landing_intro_label || "Peptides + Nutrition · One Protocol"}
              </div>
              <h1
                className={`font-bold tracking-tight leading-[1.05] ${previewMode ? "text-3xl" : "text-4xl lg:text-5xl"}`}
                style={{ color: INK }}
                data-testid="landing-headline"
              >
                {s.landing_headline || "The Amino "}
                <span style={{ color: TEAL }}>{s.landing_headline_highlight || "Exchange"}</span>
                <span className="block mt-2 text-2xl lg:text-3xl font-medium" style={{ color: INK_MUT }}>
                  {s.landing_headline_suffix || "Track your peptides. Log every meal. See the whole picture."}
                </span>
              </h1>
              <p
                className="mt-6 text-base lg:text-lg leading-relaxed max-w-lg whitespace-pre-wrap"
                style={{ color: INK_MUT }}
                data-testid="landing-body"
              >
                {s.landing_body ||
                  "One private, clinical-grade home for your peptide protocols and your daily nutrition. Log a shot, snap or dictate a meal, and watch macros, doses, streaks, and body-comp move in sync — so the details that actually change your results never fall through the cracks."}
              </p>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="landing-cta-row">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all"
                  style={{ background: TEAL, color: "#FFFFFF", boxShadow: "0 4px 14px rgba(13,148,136,0.28)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = TEAL_D)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = TEAL)}
                  data-testid="hero-get-started"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="#login-card"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-colors"
                  style={{ background: BG_SOFT, color: INK, border: `1px solid ${BORDER}` }}
                  data-testid="hero-login"
                >
                  Login
                </a>
              </div>

              {/* Stat row */}
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                <Stat value={s.landing_stat_1_value || "—"} label={s.landing_stat_1_label || ""} />
                <Stat value={s.landing_stat_2_value || "—"} label={s.landing_stat_2_label || ""} />
                <Stat value={s.landing_stat_3_value || "—"} label={s.landing_stat_3_label || ""} />
              </div>

              {news.length > 0 && (
                <div className="mt-10 pt-6" style={{ borderTop: `1px solid ${BORDER}` }} data-testid="landing-news">
                  <div className="flex items-center gap-2 mb-3">
                    <Newspaper className="w-3.5 h-3.5" style={{ color: TEAL }} />
                    <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: INK_MUT }}>Latest news</div>
                  </div>
                  <div className="space-y-3 max-h-40 overflow-y-auto pr-1">
                    {news.slice(0, 6).map((n, i) => (
                      <div key={i} className="pl-3 py-1" style={{ borderLeft: `2px solid ${TEAL}` }} data-testid={`landing-news-item-${i}`}>
                        {n.date && <div className="text-[10px] uppercase tracking-widest" style={{ color: INK_DIM }}>{n.date}</div>}
                        {n.title && <div className="text-sm font-semibold leading-snug" style={{ color: INK }}>{n.title}</div>}
                        {n.body && <div className="text-xs leading-snug mt-0.5 line-clamp-2" style={{ color: INK_MUT }}>{n.body}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: auth form card (rendered by parent as children) */}
            <div id="login-card">
              <div
                className="p-8 lg:p-10 rounded-2xl"
                style={{
                  background: CARD,
                  border: `1px solid ${BORDER}`,
                  boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
                }}
              >
                <div className="text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: TEAL }}>
                  Access
                </div>
                <h2
                  className={`font-bold tracking-tight leading-tight ${previewMode ? "text-2xl" : "text-3xl"}`}
                  style={{ color: INK }}
                  data-testid="landing-form-title"
                >
                  {s.landing_form_title || "Welcome back"}
                </h2>
                <p className="mt-2 text-sm" style={{ color: INK_MUT }} data-testid="landing-form-subtitle">
                  {s.landing_form_subtitle || "Sign in to continue your journal."}
                </p>
                {children}
                {links.length > 0 && (
                  <div className="mt-8 pt-6" style={{ borderTop: `1px solid ${BORDER}` }} data-testid="landing-social-links">
                    <div className="text-[10px] uppercase tracking-widest font-semibold mb-3" style={{ color: INK_MUT }}>Connect with us</div>
                    <div className="flex flex-wrap gap-2">
                      {links.map((l, i) => (
                        <SocialIconLink key={`${l.platform}-${i}`} platform={l.platform} url={l.url} brand={TEAL} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* -------- Feature Grid: Peptides × Nutrition -------- */}
        {!previewMode && (
        <section
          className="border-t"
          style={{ background: BG_SOFT, borderColor: BORDER }}
          data-testid="landing-feature-grid"
        >
          <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
            <div className="text-center mb-12 lg:mb-14 max-w-2xl mx-auto">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-4"
                style={{ background: TEAL_SOFT, color: TEAL }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: TEAL }} />
                What you'll track
              </div>
              <h2
                className="text-3xl lg:text-4xl font-bold tracking-tight leading-tight"
                style={{ color: INK }}
              >
                Two halves of the same protocol.
              </h2>
              <p className="mt-4 text-base leading-relaxed" style={{ color: INK_MUT }}>
                Everyone else makes you juggle a peptide tracker and a food tracker separately.
                We built one place for both — so the numbers you already care about start
                talking to each other.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-7">
              <FeatureTile
                pillar="Peptides"
                icon={Syringe}
                title="Protocols & Timers"
                blurb="Log every shot on an anatomical body diagram. Per-peptide countdowns, streak tracking, vial FIFO auto-deduction, and cycle templates that plan the next 12 weeks for you."
                testid="tile-protocols"
              />
              <FeatureTile
                pillar="Peptides"
                icon={Calculator}
                title="Reconstitution Calculator"
                blurb="Enter vial mg, BAC water, and dose — read the exact syringe units. Multi-vial split dosing, doses-per-vial, and a live syringe visualizer built for research precision."
                testid="tile-calculator"
              />
              <FeatureTile
                pillar="Nutrition"
                icon={Sparkles}
                title="AI Meal Parser"
                blurb="Type, dictate, or photograph a meal. Claude Vision + OpenAI Whisper break it down into 7 macros — calories, protein, carbs, fat, sugar, fiber, sodium — in seconds."
                testid="tile-meal-parser"
              />
              <FeatureTile
                pillar="Nutrition"
                icon={Flame}
                title="Macros & Streaks"
                blurb="Daily macro targets, weekly rollups, weight trends, and streak badges. Watch how peptide cycles line up with fat loss, muscle gain, and body-comp shifts in one view."
                testid="tile-macros"
              />
            </div>
          </div>
        </section>
        )}

        {/* -------- Testimonial -------- */}
        {!previewMode && (() => {
          // Normalize to a list.  Fall back to the single legacy field when the
          // admin has not yet populated the new `landing_testimonials` array.
          const list = Array.isArray(s.landing_testimonials) && s.landing_testimonials.length
            ? s.landing_testimonials
            : [{
                quote: s.landing_testimonial_quote,
                name: s.landing_testimonial_name,
                role: s.landing_testimonial_role,
                initials: s.landing_testimonial_initials,
              }];
          return (
            <TestimonialSection
              list={list}
              TEAL={TEAL}
              INK={INK}
              INK_MUT={INK_MUT}
              BG={BG}
              BORDER={BORDER}
            />
          );
        })()}
      </main>

      <footer className="w-full" style={{ borderTop: `1px solid ${BORDER}`, background: BG_SOFT }}>
        <div className={`mx-auto ${previewMode ? "px-6 py-4" : "max-w-6xl px-6 lg:px-8 py-6"}`} style={{ color: INK_MUT }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-center sm:text-left">
            <div>© {new Date().getFullYear()} {s.site_name || "The Amino Exchange"} · A private wellness journal</div>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:underline font-semibold" style={{ color: TEAL }} data-testid="footer-privacy-link">Privacy Policy</Link>
              <span className="text-slate-300">·</span>
              <Link to="/terms" className="hover:underline font-semibold" style={{ color: TEAL }} data-testid="footer-terms-link">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-3xl font-bold" style={{ color: TEAL, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest mt-1 font-semibold" style={{ color: INK_MUT }}>{label}</div>
    </div>
  );
}

function FeatureTile({ pillar, icon: Icon, title, blurb, testid }) {
  const { resolved } = useTheme();
  const isDark = resolved === "dark";
  const isPeptide = pillar === "Peptides";
  const accent = isPeptide ? (isDark ? "#2DD4BF" : "#0D9488") : (isDark ? "#5EEAD4" : "#0F766E");
  const accentSoft = isDark ? "rgba(20,184,166,0.14)" : (isPeptide ? "#F0FDFA" : "#ECFDF5");
  const surface = isDark ? "#111827" : "#FFFFFF";
  const surfaceEdge = isDark ? "#1E293B" : "#E2E8F0";
  const restShadow = isDark ? "0 2px 12px rgba(0,0,0,0.28)" : "0 2px 12px rgba(15,23,42,0.04)";
  return (
    <div
      className="group relative rounded-2xl p-7 lg:p-8 transition-all"
      style={{
        background: surface,
        border: `1px solid ${surfaceEdge}`,
        boxShadow: restShadow,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = accent;
        e.currentTarget.style.boxShadow = `0 8px 32px ${accent}${isDark ? "33" : "18"}`;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = surfaceEdge;
        e.currentTarget.style.boxShadow = restShadow;
        e.currentTarget.style.transform = "translateY(0)";
      }}
      data-testid={testid}
    >
      {/* Pillar chip — instantly signals Peptides vs Nutrition */}
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest mb-5"
        style={{ background: accentSoft, color: accent }}
      >
        <span className="w-1 h-1 rounded-full" style={{ background: accent }} />
        {pillar}
      </div>

      {/* Icon square */}
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{
          background: isDark
            ? `linear-gradient(135deg,${accentSoft} 0%,#111827 100%)`
            : `linear-gradient(135deg,${accentSoft} 0%,#FFFFFF 100%)`,
          border: `1px solid ${accent}22`,
        }}
      >
        <Icon className="w-6 h-6" style={{ color: accent }} strokeWidth={2} />
      </div>

      <h3 className="text-xl lg:text-2xl font-bold tracking-tight leading-tight text-slate-900">
        {title}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">
        {blurb}
      </p>
    </div>
  );
}

const DEFAULT_TESTIMONIAL = {
  quote: "\u201CI used to keep peptide logs in a notebook and macros in a spreadsheet. Having both live side-by-side finally showed me why my body-comp was stuck — I was under-eating on cycle days. Two weeks later, everything moved.\u201D",
  name: "Marcus K.",
  role: "Retatrutide protocol · 14-week cut",
  initials: "MK",
};

function TestimonialSection({ list, TEAL, INK, INK_MUT, BG, BORDER }) {
  // Filter to items with actual content; use the default if the array is empty.
  const items = (list || []).map((t) => ({
    quote: t?.quote?.trim() || "",
    name: t?.name?.trim() || "",
    role: t?.role?.trim() || "",
    initials: (t?.initials || "").trim().slice(0, 3).toUpperCase(),
  })).filter((t) => t.quote);
  const clean = items.length ? items : [DEFAULT_TESTIMONIAL];
  const [idx, setIdx] = useState(0);

  // Auto-rotate every 9s (only when there are multiple entries).
  useEffect(() => {
    if (clean.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % clean.length), 9000);
    return () => clearInterval(t);
  }, [clean.length]);

  const active = clean[idx] || clean[0];
  const initials = active.initials || (active.name ? active.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase() : "MK");

  return (
    <section
      className="border-t"
      style={{ background: BG, borderColor: BORDER }}
      data-testid="landing-testimonial"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 lg:py-20 text-center">
        <Quote
          className="w-8 h-8 mx-auto mb-6 opacity-60"
          style={{ color: TEAL }}
          strokeWidth={2}
        />
        {/* Crossfading quote block — key by index so React re-mounts the div and
            triggers the CSS opacity transition on each swap. */}
        <div key={idx} className="testimonial-fade" data-testid={`testimonial-slide-${idx}`}>
          <blockquote
            className="text-2xl lg:text-3xl font-medium leading-snug tracking-tight max-w-3xl mx-auto"
            style={{ color: INK }}
            data-testid="testimonial-quote"
          >
            {active.quote}
          </blockquote>
          <div className="mt-8 flex items-center justify-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm"
              style={{
                background: "linear-gradient(135deg,#CBD5E1 0%,#94A3B8 100%)",
                color: "#FFFFFF",
                boxShadow: "0 2px 8px rgba(15,23,42,0.15)",
              }}
              aria-hidden
              data-testid="testimonial-avatar"
            >
              {initials}
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold" style={{ color: INK }} data-testid="testimonial-name">
                {active.name || "\u00A0"}
              </div>
              {active.role && (
                <div className="text-xs mt-0.5" style={{ color: INK_MUT }} data-testid="testimonial-role">
                  {active.role}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Dot indicators — only when multiple */}
        {clean.length > 1 && (
          <div className="mt-8 flex items-center justify-center gap-1.5" data-testid="testimonial-dots">
            {clean.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Show testimonial ${i + 1}`}
                className="rounded-full transition-all"
                style={{
                  width: i === idx ? 24 : 6,
                  height: 6,
                  background: i === idx ? TEAL : "#CBD5E1",
                }}
                data-testid={`testimonial-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export { LandingHero };
